import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Warning } from "@phosphor-icons/react"
import { Question } from "@phosphor-icons/react/dist/ssr"
import { FormError } from "@/components/ui/form"
import { Tag } from "@/components/ui/tag"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { formatDateString } from "@/lib/format-date"
import { QuestionnaireFormType } from "@/modules/patients/detail/progress-note/components/questionnaire"
import { QuestionnaireDrawer } from "@/modules/patients/detail/progress-note/components/questionnaire-drawer"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { QuestionnaireCriticalitySummaryFragment$data } from "@/modules/patients/services/__generated__/QuestionnaireCriticalitySummaryFragment.graphql"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface QuestionnaireFormProps {
  appointment: EpisodeOfCareProgressNoteData["appointment"]
  criticalitySummary: QuestionnaireCriticalitySummaryFragment$data | null | undefined
  progressNoteId: string
  diagnosisCode?: string
}

const QuestionnaireDrawerName = "QuestionnaireDrawer"

const QuestionnaireForm = (props: QuestionnaireFormProps) => {
  const { appointment, criticalitySummary, progressNoteId, diagnosisCode } = props

  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const drawerName = useDrawerStore((state) => state.drawerName)
  const { t } = useTranslation()

  const form = useFormContext<QuestionnaireFormType>()

  if (!criticalitySummary) {
    return null
  }

  const { useCaseName, criticals, warnings, infos, questionnaireType } = criticalitySummary

  const {
    control,
    formState: { errors: formErrors },
  } = form

  const handleOpenQuestionnaireDrawer = () => {
    if (drawerName === QuestionnaireDrawerName) return
    openDrawer(
      () => (
        <QuestionnaireDrawer
          completedAt={appointment.createdAt}
          isReadOnly={false}
          appointmentId={appointment.id}
          questionnaireId={appointment.questionnaireId}
          isCompletedAppointment={appointment.isCompleted}
          channel={appointment.channel}
          requestType={appointment.requestType}
          name={useCaseName}
          type={questionnaireType}
          progressNoteId={progressNoteId}
          diagnosisCode={diagnosisCode}
        />
      ),
      QuestionnaireDrawerName,
    )
  }

  return (
    <div className="grid">
      <Controller
        name="questionnaireStatus"
        control={control}
        render={() => (
          <button
            type="button"
            className={`questionSummaryButton hover:bg-surface-hovered flex cursor-pointer items-center justify-between gap-1 rounded-lg px-2 py-1 ${
              formErrors.questionnaireStatus?.message ? "border-critical border-2" : ""
            }`}
            onClick={() => {
              FaroInsightsWrapper.pushEvent("questionnaires_open")
              handleOpenQuestionnaireDrawer()
            }}>
            <div className="flex items-center gap-2">
              <Question className="text-on-surface-subdued h-6 w-6 shrink-0" />
              <span className="flex flex-col items-start text-left">
                <p>
                  {t("patient.questionnaire.title")}: {useCaseName}
                </p>
                <p className="text-on-surface-subdued text-xs">
                  {t("patient.questionnaire.completedAt")} {formatDateString(appointment.createdAt)}
                </p>
              </span>
            </div>
            <div className="flex items-center gap-2">
              {appointment.asyncRequestStatus === "APPROVED" && (
                <Tag hasIcon={false} type="success" className="ml-auto" label={t("common.button.approved")} />
              )}
              {appointment.asyncRequestStatus === "REJECTED" && (
                <Tag hasIcon={false} type="critical" className="ml-auto" label={t("common.button.rejected")} />
              )}
              <div className="flex items-end gap-2">
                {criticals > 0 && (
                  <Tag hasIcon={true} type="critical" className="ml-auto" label={criticals.toString()} />
                )}
                {warnings > 0 && <Tag hasIcon={true} type="warning" className="ml-auto" label={warnings.toString()} />}
                {infos > 0 && (
                  <Tag
                    hasIcon={true}
                    icon={<Warning size={16} />}
                    type="neutral"
                    className="ml-auto"
                    label={infos.toString()}
                  />
                )}
              </div>
            </div>
          </button>
        )}
      />
      {formErrors.questionnaireStatus?.message && <FormError>{t(formErrors.questionnaireStatus?.message)}</FormError>}
    </div>
  )
}

export { QuestionnaireForm }
